import { ACTION_EVENT_CLICK_BOOKING_BUTTON } from 'utils/analytics/eventTypeConstantsAmplitude';
import { Button } from 'omakase';
import { ButtonCoreVariant } from 'omakase/Button/ButtonCore';
import { Card } from 'ui';
import { CardHeader } from '@material-ui/core';
import { Theme, css } from '@emotion/react';
import {
  customBase,
  muiCardHeaderActionStylesOverride,
} from 'components/business/search/booking-card/bookingCardContentCssDefs';
import { ifClickable } from '@tock/shared/utils/touchUtils';
import { primaryChildContainer, primaryContainer } from 'omakase/Collapse/collapseCssDefs';
import { useConsumerConversionAnalytics } from 'hooks/analytics/useConsumerConversionAnalytics';
import React, { FC } from 'react';

const noop = () => undefined;
export type BookingCardStatus = 'AVAILABLE' | 'LOCKED_BY_USER' | 'DISABLED' | 'SOLD_OUT';

// Exported for testing
export interface BookingCardContentProps {
  bookingCardStatus: BookingCardStatus;
  'button-aria-label'?: string;
  buttonHidden?: boolean;
  buttonIcon?: React.ReactNode;
  buttonLabel: string;
  isDisabled?: boolean;
  handleClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  mainContent: React.ReactNode;
}

export const BookingCardContent: FC<BookingCardContentProps> = ({
  bookingCardStatus,
  'button-aria-label': buttonAriaLabel,
  buttonHidden = false,
  buttonIcon,
  buttonLabel,
  isDisabled = false,
  handleClick,
  mainContent,
  children,
}) => {
  const dataTestIdPrefix = 'search-result';
  const sendConversionAnalytics = useConsumerConversionAnalytics();

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); // Prevent the parent (card header) from being clicked as well
    handleClick(e);

    // Event Page ticket click booking amplitude analytics
    sendConversionAnalytics(ACTION_EVENT_CLICK_BOOKING_BUTTON);
  };

  const getVariantForStatus = (status: string): ButtonCoreVariant => {
    switch (status) {
      case 'LOCKED_BY_USER':
        return 'success';
      case 'DISABLED':
      case 'SOLD_OUT':
        return 'basic';
      default:
        return 'primary';
    }
  };

  return (
    <Card
      variant="outlined"
      css={(theme: Theme) => [customBase(theme)]}
      data-testid={dataTestIdPrefix}
    >
      <CardHeader
        data-testid={`${dataTestIdPrefix}-list-item`}
        onClick={!isDisabled ? ifClickable(handleClick) : noop}
        css={() => [
          primaryContainer,
          muiCardHeaderActionStylesOverride,
          isDisabled &&
            css`
              cursor: default;
            `,
        ]}
        action={
          !buttonHidden && (
            <Button
              iconStart={buttonIcon}
              aria-label={buttonAriaLabel}
              data-testid="booking-card-button"
              size="medium"
              variant={getVariantForStatus(bookingCardStatus)}
              disabled={bookingCardStatus === 'DISABLED'}
              onClick={onClick}
            >
              {buttonLabel}
            </Button>
          )
        }
        title={
          <section
            css={[{ display: 'flex', justifyContent: 'space-between' }, primaryChildContainer]}
            data-testid={`${dataTestIdPrefix}-title`}
          >
            {mainContent}
          </section>
        }
      />
      {children}
    </Card>
  );
};
